import { useEffect, useRef, type RefObject } from 'react';

const ORIGIN = 'https://www.loom.com';
const VERSION = '0.0.11';

type LoomMsg = {
  context: string;
  event?: string;
  value?: { seconds?: number; duration?: number };
};

type Args = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  threshold: number;
  onWatched: () => void;
  onProgress?: (elapsed: number, duration: number) => void;
};

type State = { watched: number; lastT: number; fired: boolean };

type TickArgs = {
  state: State;
  m: LoomMsg;
  threshold: number;
  fire: () => void;
  onProgress?: ((e: number, d: number) => void) | undefined;
};

function post(iframe: HTMLIFrameElement | null, value: string): void {
  iframe?.contentWindow?.postMessage(
    JSON.stringify({
      context: 'player.js',
      version: VERSION,
      method: 'addEventListener',
      value,
    }),
    ORIGIN
  );
}

function parseMsg(data: unknown): LoomMsg | null {
  if (typeof data !== 'string') return null;
  try {
    const m = JSON.parse(data) as LoomMsg;
    return m?.context === 'player.js' ? m : null;
  } catch {
    return null;
  }
}

function tick(a: TickArgs): void {
  const t = a.m.value?.seconds;
  const d = a.m.value?.duration;
  if (t == null || !d) return;
  const dt = t - a.state.lastT;
  if (dt > 0 && dt < 2) a.state.watched += dt;
  a.state.lastT = t;
  a.onProgress?.(a.state.watched, d);
  if (!a.state.fired && a.state.watched / d >= a.threshold) {
    a.state.fired = true;
    a.fire();
  }
}

function makeListener(
  iframe: HTMLIFrameElement | null,
  args: TickArgs
): (e: globalThis.MessageEvent) => void {
  return (e) => {
    if (e.origin !== ORIGIN) return;
    const m = parseMsg(e.data);
    if (!m) return;
    if (m.event === 'ready') {
      ['play', 'pause', 'ended', 'timeupdate'].forEach((ev) =>
        post(iframe, ev)
      );
    } else if (m.event === 'timeupdate') {
      tick({ ...args, m });
    } else if (m.event === 'ended' && !args.state.fired) {
      args.state.fired = true;
      args.fire();
    }
  };
}

export function useLoomTracker(args: Args): void {
  const cbRef = useRef(args.onWatched);
  const progressRef = useRef(args.onProgress);
  useEffect(() => {
    cbRef.current = args.onWatched;
    progressRef.current = args.onProgress;
  }, [args.onWatched, args.onProgress]);

  useEffect(() => {
    const state: State = { watched: 0, lastT: 0, fired: false };
    const tickArgs: TickArgs = {
      state,
      m: { context: 'player.js' },
      threshold: args.threshold,
      fire: () => cbRef.current(),
      onProgress: (e, d) => progressRef.current?.(e, d),
    };
    const onMsg = makeListener(args.iframeRef.current, tickArgs);
    window.addEventListener('message', onMsg);
    return (): void => window.removeEventListener('message', onMsg);
  }, [args.iframeRef, args.threshold]);
}
